/**
 * useHelpRecents — état + persistance des « récents » bicéphales du Help Drawer
 * (lot §5). Gère la liste localStorage (recherches + conversations) et le cycle de
 * vie du fil IA courant (id stable, upsert à chaque tour terminé).
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { UseHelpAssistantReturn } from './useHelpAssistant'
import {
    clearRecents as storeClear,
    loadRecents,
    recordConversation as storeConversation,
    recordSearch as storeSearch,
    removeRecent as storeRemove,
    type RecentEntry,
} from '../lib/helpSearchHistory'

function newConversationId(): string {
    const c = globalThis.crypto
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()
    return `c_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export interface UseHelpRecentsReturn {
    recents: RecentEntry[]
    /** Mémorise une recherche validée (article ouvert depuis la recherche). */
    recordSearchQuery: (query: string) => void
    removeEntry: (entry: RecentEntry) => void
    clearAll: () => void
    /** Démarre l'enregistrement d'un NOUVEAU fil (id neuf). */
    beginConversation: () => void
    /** Garantit un id de fil (1ʳᵉ question après « Nouvelle conversation »). */
    ensureConversation: () => void
    /** Reprend un fil archivé (réutilise son id pour le faire grossir en place). */
    adoptConversation: (id: string) => void
    /** Oublie le fil courant (après reset). */
    endConversation: () => void
}

export function useHelpRecents(
    userId: string | undefined,
    isOpen: boolean,
    assistant: UseHelpAssistantReturn,
    assistantActive: boolean,
): UseHelpRecentsReturn {
    const [recents, setRecents] = useState<RecentEntry[]>([])
    const conversationIdRef = useRef<string | null>(null)

    // (Re)charge l'historique à l'ouverture ; oublie le fil courant à la fermeture.
    useEffect(() => {
        if (isOpen) setRecents(loadRecents(userId))
        else conversationIdRef.current = null
    }, [isOpen, userId])

    // Upsert du fil courant dès qu'un tour assistant se termine (réponse complète).
    useEffect(() => {
        const id = conversationIdRef.current
        if (!id || !assistantActive || assistant.isStreaming) return
        const msgs = assistant.messages
        if (msgs.length === 0) return
        const last = msgs[msgs.length - 1]
        if (last.role !== 'assistant' || !last.content) return
        const title = (msgs.find((m) => m.role === 'user')?.content ?? '').slice(0, 120)
        setRecents(storeConversation(userId, id, msgs, title))
    }, [assistant.isStreaming, assistant.messages, assistantActive, userId])

    const recordSearchQuery = useCallback(
        (query: string) => setRecents(storeSearch(userId, query)),
        [userId],
    )
    const removeEntry = useCallback(
        (entry: RecentEntry) => setRecents(storeRemove(userId, entry)),
        [userId],
    )
    const clearAll = useCallback(() => {
        storeClear(userId)
        setRecents([])
    }, [userId])

    const beginConversation = useCallback(() => {
        conversationIdRef.current = newConversationId()
    }, [])
    const ensureConversation = useCallback(() => {
        if (!conversationIdRef.current) conversationIdRef.current = newConversationId()
    }, [])
    const adoptConversation = useCallback((id: string) => {
        conversationIdRef.current = id
    }, [])
    const endConversation = useCallback(() => {
        conversationIdRef.current = null
    }, [])

    return {
        recents,
        recordSearchQuery,
        removeEntry,
        clearAll,
        beginConversation,
        ensureConversation,
        adoptConversation,
        endConversation,
    }
}
